import { memo, useState } from "react"

import { useTranslation } from "react-i18next"

import type { TokenMetricsType } from "../types"

import { Activity, ChevronDown, ChevronUp } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { EmptyState } from "@/components/ui/empty-state"

export const TokenMetricsByModel = memo(function TokenMetricsByModel({
  metrics,
  currency = "USD",
}: {
  metrics: TokenMetricsType
  currency?: "SEK" | "USD" | "EUR"
}) {
  const { t } = useTranslation()
  const [isExpanded, setIsExpanded] = useState(false)

  const getCurrencySymbol = (curr: string) => {
    switch (curr) {
      case "SEK":
        return "kr"
      case "EUR":
        return "€"
      case "USD":
      default:
        return "$"
    }
  }

  const currencySymbol = getCurrencySymbol(currency)

  if (metrics.byModel.length === 0) {
    return (
      <EmptyState
        description={t("adminDashboard.tokenMetrics.emptyDescription")}
        icon={Activity}
        title={t("adminDashboard.tokenMetrics.emptyTitle")}
      />
    )
  }

  const displayModels = isExpanded ? metrics.byModel : metrics.byModel.slice(0, 3)
  const hasMoreModels = metrics.byModel.length > 3

  return (
    <div className="space-y-4">
      {/* Summary */}
      <div className="grid grid-cols-2 gap-4 rounded-lg border p-3 md:grid-cols-5">
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.tokenMetrics.totalTokens")}
          </div>
          <div className="text-lg font-semibold">{metrics.totalTokens.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.tokenMetrics.totalRequests")}
          </div>
          <div className="text-lg font-semibold">{metrics.totalRequests.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.tokenMetrics.cacheEfficiency")}
          </div>
          <div className="text-lg font-semibold">{metrics.cacheEfficiency.toFixed(1)}%</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.tokenMetrics.cachedTokens")}
          </div>
          <div className="text-lg font-semibold">{metrics.totalCachedTokens.toLocaleString()}</div>
        </div>
        <div>
          <div className="text-muted-foreground text-xs">
            {t("adminDashboard.tokenMetrics.totalCost")}
          </div>
          <div className="text-lg font-semibold">
            {currencySymbol}
            {metrics.totalCostUsd.toFixed(2)}
          </div>
        </div>
      </div>

      {/* Hosted agents (HERMES Fas 6): SEK-native run usage, separate from
          the per-model split (run usage carries no model dimension). */}
      {metrics.hostedAgents && metrics.hostedAgents.run_count > 0 ? (
        <div className="rounded-lg border p-3">
          <div className="mb-2 flex items-center justify-between">
            <span className="font-medium">{t("adminDashboard.tokenMetrics.hostedAgents")}</span>
            <span className="text-muted-foreground text-sm">
              {metrics.hostedAgents.run_count.toLocaleString()}{" "}
              {t("adminDashboard.tokenMetrics.hostedAgentRuns")}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            <div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.tokenMetrics.totalTokens")}
              </div>
              <div className="font-medium">
                {metrics.hostedAgents.total_tokens.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="text-muted-foreground text-xs">
                {t("adminDashboard.tokenMetrics.hostedAgentCostSek")}
              </div>
              <div className="font-medium">{metrics.hostedAgents.cost_sek.toFixed(2)} kr</div>
            </div>
          </div>
        </div>
      ) : null}

      {/* Per Model Breakdown */}
      <div className="space-y-3">
        {displayModels.map((model) => (
          <div key={model.model} className="rounded-lg border p-3">
            <div className="mb-2 flex items-center justify-between">
              <span className="font-medium">{model.model}</span>
              <span className="text-muted-foreground text-sm">
                {model.request_count.toLocaleString()} {t("adminDashboard.tokenMetrics.requests")}
              </span>
            </div>
            <div className="grid grid-cols-2 gap-2 text-sm md:grid-cols-5">
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.tokenMetrics.input")}
                </div>
                <div className="font-medium">{model.input_tokens.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.tokenMetrics.output")}
                </div>
                <div className="font-medium">{model.output_tokens.toLocaleString()}</div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {model.reasoning_tokens > 0 ? t("adminDashboard.tokenMetrics.reasoning") : "—"}
                </div>
                <div className="font-medium">
                  {model.reasoning_tokens > 0 ? model.reasoning_tokens.toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {model.cached_tokens > 0
                    ? t("adminDashboard.tokenMetrics.cached", {
                        percentage: model.cached_percentage.toFixed(1),
                      })
                    : "—"}
                </div>
                <div className="font-medium">
                  {model.cached_tokens > 0 ? model.cached_tokens.toLocaleString() : "—"}
                </div>
              </div>
              <div>
                <div className="text-muted-foreground text-xs">
                  {t("adminDashboard.tokenMetrics.estimatedCost")}
                </div>
                <div className="font-medium">
                  {currencySymbol}
                  {model.estimated_cost_usd.toFixed(2)}
                </div>
              </div>
            </div>
            <div className="mt-2">
              <div className="bg-muted h-2 w-full overflow-hidden rounded-full">
                <div
                  className="bg-primary h-full"
                  style={{
                    width: `${(model.total_tokens / metrics.totalTokens) * 100}%`,
                  }}
                />
              </div>
              <div className="text-muted-foreground mt-1 text-xs">
                {model.total_tokens.toLocaleString()} tokens (
                {((model.total_tokens / metrics.totalTokens) * 100).toFixed(1)}%)
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Expand/Collapse Button */}
      {hasMoreModels && (
        <div className="flex justify-center">
          <Button className="w-full" variant="outline" onClick={() => setIsExpanded(!isExpanded)}>
            {isExpanded ? (
              <>
                <ChevronUp className="mr-2 size-4" />
                {t("adminDashboard.tokenMetrics.showLess")}
              </>
            ) : (
              <>
                <ChevronDown className="mr-2 size-4" />
                {t("adminDashboard.tokenMetrics.showMore", { count: metrics.byModel.length - 3 })}
              </>
            )}
          </Button>
        </div>
      )}
    </div>
  )
})
